import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Riptide Vortex (Maelstrom Verge) — the first pattern in the game that
 * manipulates the PLAYER's position rather than only spawning hazards: a
 * whirlpool opens on the player's location and continuously drags them
 * toward its core while spiral arms of bullets pour out of the center.
 * The pull is always weaker than the player's own move speed (fighting
 * the current works) and dashing ignores it entirely (i-frames read as
 * "cutting through the water"), so it pressures positioning without ever
 * being inescapable. Standing in the core itself is the real punishment.
 */
export class RiptideVortex extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Riptide Vortex', telegraphDuration: 1.1, activeDuration: 3.4, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin(_boss, player) {
    // Opens exactly where the player is standing — the fair version of a
    // trap: you always start dead-center and the whole pattern is about
    // getting (and staying) out.
    this._cx = player.x;
    this._cy = player.y;
    this._spin = Math.random() * Math.PI * 2;
    this._spitTimer = 0;
    this._coreTimer = 0;
    this._arm = 0;
  }

  onActiveTick(dt, boss, player, pool) {
    this._spin += dt * 2.4;
    this._coreTimer -= dt;
    this._spitTimer -= dt;

    const dx = this._cx - player.x;
    const dy = this._cy - player.y;
    const dist = Math.hypot(dx, dy);

    // The current: pull strength eases off toward the rim so escape gets
    // easier the farther out you fight, never harder.
    if (dist > 1 && dist < this.config.pullRadius && !player.isDashing) {
      const strength = this.config.pullStrength * (0.55 + 0.45 * (1 - dist / this.config.pullRadius));
      player.x += (dx / dist) * strength * dt;
      player.y += (dy / dist) * strength * dt;
      player.x = Math.min(ARENA.x + ARENA.width - player.radius, Math.max(ARENA.x + player.radius, player.x));
      player.y = Math.min(ARENA.y + ARENA.height - player.radius, Math.max(ARENA.y + player.radius, player.y));
    }

    if (dist < this.config.coreRadius && this._coreTimer <= 0) {
      player.takeDamage(this.config.coreDamage);
      this._coreTimer = 0.5;
    }

    if (this._spitTimer <= 0) {
      this._spitTimer = Math.max(0.2, this.config.spitInterval - boss.phase * 0.04);
      // Two opposed spiral arms — successive shots at an advancing angle
      // trace curved arms outward from the core without any per-bullet
      // steering.
      this._arm += 0.62;
      for (let k = 0; k < 2; k++) {
        const angle = this._arm + k * Math.PI;
        pool.fire({
          x: this._cx,
          y: this._cy,
          vx: Math.cos(angle) * this.config.bulletSpeed,
          vy: Math.sin(angle) * this.config.bulletSpeed,
          radius: 2.5,
          damage: this.config.bulletDamage,
          color: '#4dd8c7',
          lifetime: 3.2,
        });
      }
    }
  }

  getVisual() {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'vortex',
      x: this._cx,
      y: this._cy,
      pullRadius: this.config.pullRadius,
      coreRadius: this.config.coreRadius,
      spin: this._spin,
      t: this.state === PatternState.TELEGRAPH ? 1 - this._timer / this.telegraphDuration : 1,
      active: this.state === PatternState.ACTIVE,
    };
  }
}
